import {
  ChangeEvent,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { usePopperTooltip } from 'react-popper-tooltip';

import { EMPTY_OPTION, KEYBOARD } from 'commons';
import { Checkbox } from 'components';
import { FormOption } from 'types';

import { ClearButton, Input, ScanCodesButton, Tags } from './components';
import { useSelect } from './hooks';
import { SelectProvider } from './providers';
import * as S from './styles';

interface WrapperProps {
  name: string;
  onChange: (e: string | ChangeEvent<any> | HTMLInputElement | Event) => void;
  options: FormOption[];
  initialTagged?: FormOption[];
  initialSelected?: FormOption;
  status?: 'error' | 'success' | 'default';
  multiple?: boolean;
  searchable?: boolean;
  autoFocus?: boolean;
  topPosition?: boolean;
  children: ReactNode;
  'data-testid'?: string;
  disabled?: boolean;
}

function Wrapper({
  children,
  initialSelected,
  initialTagged,
  ...rest
}: WrapperProps) {
  return (
    <SelectProvider
      initialSelected={initialSelected}
      initialTagged={initialTagged}
    >
      <CompositionSelect {...rest}>{children}</CompositionSelect>
    </SelectProvider>
  );
}

function CompositionSelect({
  name,
  onChange,
  options,
  status = 'default',
  multiple = false,
  searchable = false,
  autoFocus = false,
  children,
  topPosition = false,
  disabled = false,
  ...rest
}: WrapperProps) {
  const {
    selectInputRef,
    showOptions,
    setShowOptions,
    option,
    setOption,
    tagList,
    setTagList,
    inputText,
    setInputText,
    setSearchable,
  } = useSelect();

  const [, updateState] = useState({});

  const hoverIndex = useRef(0);
  const availableOptions = useRef(options);
  const firstRender = useRef<boolean>(true);

  const {
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    triggerRef: toggleDropdownRef,
    tooltipRef: optionsRef,
  } = usePopperTooltip(
    {
      offset: [0, 0],
    },
    {
      placement: topPosition ? 'top-start' : 'bottom-start',
      strategy: 'fixed',
    }
  );

  function customHandleChange(value: string) {
    var onChangeEvent = new Event('change', { bubbles: true });
    const el = document.createElement('input');
    el.id = name;
    el.name = name;
    el.value = value;
    el.addEventListener('change', onChange);
    el.dispatchEvent(onChangeEvent);
  }

  function choseOption(option: FormOption) {
    if (multiple) {
      setOption(EMPTY_OPTION);

      if (setTagList)
        setTagList(currentTagList => {
          let alreadyExist = false;

          const newTagList = currentTagList.filter((tag: FormOption) => {
            if (tag.value === option.value) {
              alreadyExist = true;
              return false;
            }
            return true;
          });

          if (!alreadyExist) {
            newTagList.push(option);
          }

          return newTagList;
        });
    } else {
      setOption(option);
      setInputText(option.label);

      setTimeout(() => {
        setShowOptions(false);

        toggleDropdownRef?.focus();
      }, 10);
    }
  }

  useEffect(() => {
    if (firstRender.current) return;

    const newSelected = option.value;

    customHandleChange(newSelected);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  useEffect(() => {
    if (firstRender.current) return;

    const newSelectedList = tagList.map(mapTag => mapTag.value).join(',');

    customHandleChange(newSelectedList);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagList]);

  useEffect(() => {
    if (firstRender.current) {
      setSearchable(searchable);
    }

    firstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  useEffect(() => {
    availableOptions.current = options;
  }, [options]);

  useEffect(() => {
    if (searchable) {
      const newAvailableOptions = options.filter(
        filterOption =>
          !inputText ||
          filterOption.label.toLowerCase().includes(inputText.toLowerCase())
      );

      hoverIndex.current = 0;
      availableOptions.current = newAvailableOptions;

      updateState({});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText]);

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      const { key } = event;

      if (key === KEYBOARD.tab || key === KEYBOARD.space) {
        event.stopPropagation();
        event.preventDefault();
      }

      switch (key) {
        case KEYBOARD.arrowUp:
          if (hoverIndex.current <= 0) {
            hoverIndex.current = availableOptions.current.length - 1;
          } else {
            hoverIndex.current--;
          }
          optionsRef?.children?.[hoverIndex.current]?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth',
          });

          updateState({});

          break;

        case KEYBOARD.arrowDown:
          if (hoverIndex.current >= availableOptions.current.length - 1) {
            hoverIndex.current = 0;
          } else {
            hoverIndex.current++;
          }
          optionsRef?.children?.[hoverIndex.current]?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth',
          });

          updateState({});

          break;

        case KEYBOARD.enter:
          if (availableOptions.current.length === 0) break;

          const newOption = availableOptions.current[hoverIndex.current];
          choseOption(newOption);
          updateState({});

          break;

        case KEYBOARD.esc:
        case KEYBOARD.tab:
          setShowOptions(false);

          toggleDropdownRef?.focus();

          break;

        default:
          break;
      }
    }

    function handleClickOutside(event: MouseEvent) {
      if (
        toggleDropdownRef &&
        !toggleDropdownRef.contains(event.target as Node) &&
        optionsRef &&
        !optionsRef.contains(event.target as Node)
      ) {
        setShowOptions(false);
        toggleDropdownRef?.focus();
      }
    }

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOptions, optionsRef]);

  return (
    <S.Main
      $disabled={disabled}
      id={name}
      onKeyDown={
        showOptions
          ? undefined
          : (event: React.KeyboardEvent) => {
              const { key } = event;
              const { space, enter, arrowDown, arrowUp } = KEYBOARD;

              if (key === space) {
                event.stopPropagation();
                event.preventDefault();
              }

              if (toggleDropdownRef && toggleDropdownRef === event.target) {
                if ([enter, arrowDown, arrowUp].includes(key)) {
                  if (multiple) hoverIndex.current = 0;

                  setTimeout(() => {
                    setShowOptions(true);
                  }, 10);
                }
              }
            }
      }
      {...rest}
    >
      <S.WrapperButton
        disabled={disabled}
        autoFocus={autoFocus}
        $status={status}
        onKeyUp={(event: React.KeyboardEvent) => {
          if (showOptions) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onKeyDown={(event: React.KeyboardEvent) => {
          const { key } = event;
          const { enter, tab, shift } = KEYBOARD;

          if (key === enter) {
            event.preventDefault();
          }

          if (key !== enter && key !== tab && key !== shift) {
            selectInputRef?.current?.focus();

            setShowOptions(true);
          }
        }}
        onClick={(event: React.MouseEvent) => {
          event.preventDefault();

          selectInputRef?.current?.focus();

          setTimeout(() => {
            setShowOptions(cur => {
              if (!cur && multiple) {
                hoverIndex.current = 0;
              } else {
                setTimeout(() => {
                  optionsRef?.children?.[hoverIndex.current]?.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest',
                    behavior: 'smooth',
                  });
                });
              }

              return !cur;
            });
          });
        }}
        ref={setTriggerRef}
        type="button"
      >
        {children}
      </S.WrapperButton>

      {showOptions && (
        <S.Listbox
          $topPosition={topPosition}
          ref={setTooltipRef}
          aria-label="select"
          {...getTooltipProps({
            className: 'select-dropdown',
            style: {
              ...getTooltipProps().style,
              width: toggleDropdownRef?.offsetWidth,
            },
          })}
        >
          {availableOptions.current.map((mapOption, index) => {
            const { label, value, testId } = mapOption;
            const isSelected = tagList.some(
              tag => tag.value === mapOption.value
            );

            return (
              <S.Option
                key={value}
                onClick={() => choseOption(mapOption)}
                onMouseMove={() => {
                  if (hoverIndex.current !== index) {
                    hoverIndex.current = index;
                    updateState({});
                  }
                }}
                $hovered={hoverIndex.current === index}
                $selected={multiple ? isSelected : option.value === value}
                data-testid={testId}
              >
                {multiple && <Checkbox readOnly checked={isSelected} />}

                {label}
              </S.Option>
            );
          })}
        </S.Listbox>
      )}
    </S.Main>
  );
}

function Icon({ children }: HTMLAttributes<HTMLDivElement>) {
  return <S.Icon>{children}</S.Icon>;
}

function StatusArrow() {
  const { showOptions } = useSelect();

  return (
    <S.Icon>
      {showOptions ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
    </S.Icon>
  );
}

export const Select = {
  Wrapper,
  Input,
  Icon,
  Tags,
  ClearButton,
  StatusArrow,
  ScanCodesButton,
};
